'use client';
import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Navbar } from '../../components/layout/navbar';
import { Footer } from '../../components/layout/footer';
import { ReportDialog } from '../../components/modals/report-dialog';
import { getDiscussionPath } from '../../utils/slug';
import {
  Users,
  Calendar,
  Edit,
  Eye,
  PenSquare,
  User,
  Globe,
  TrendingUp,
  MessageSquare,
  BookOpen,
  Heart,
  Star,
  Settings,
  Share2,
  MoreVertical,
  Trash2,
  AlertCircle,
  Loader2,
  ThumbsUp,
  ThumbsDown,
  Clock,
  ShieldAlert,
  Flag,
  RotateCcw,
  Zap,
  Activity,
  Layout,
  CheckCircle2,
  Box,
  Hash,
  Fingerprint,
  Crown,
  LayoutGrid,
  Copy,
  Award,
  Lock,
  ChevronRight,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../components/ui/popover';
import { EditProfileModal } from '../../components/modals/edit-profile-modal';
import { MissionStatementModal } from '../../components/modals/mission-statement-modal';
import { useAuthStore } from '../../stores';
import { useGetUserById, useUpdateUserProfile } from '../../hooks/use-user';
import { useArticlesByCreator } from '../../hooks/use-articles';
import { useGetCommentsByUser } from '../../hooks/use-comments';
import {
  useGetAllInteractionsByUserId,
  useGetInteractionsGivenByUser,
  useGetViewStats,
} from '../../hooks/use-user';
import { useForum } from '../../hooks/use-forum';
import { useQuery } from '@tanstack/react-query';
import axios from '../../utils/axios-config';
import { API_ENDPOINTS } from '../../config/api-config';
import { Pagination } from '../../components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  useBadges,
  useUserBadges,
  getBadgeTierStyles,
  getBadgeGroupName,
} from '../../hooks/use-badges';
import { Progress } from '../../components/ui/progress';
import { cn } from '../../lib/utils';
import { getUserAvatar, MEDIA_DEFAULTS } from '../../config/media-defaults';

const ParticleDot = () => {
  return (
    <motion.div
      className="h-6 w-6 rounded-full bg-zinc-900 border border-white/10 flex items-center justify-center relative z-20 cursor-pointer group"
      whileHover="hover"
      initial="initial"
    >
      <div className="h-2 w-2 rounded-full bg-violet-500 relative z-10 group-hover:bg-violet-400 transition-colors" />

      {/* Ripple/Glow */}
      <motion.div
        variants={{
          hover: { scale: 1.5, opacity: 0.5 },
          initial: { scale: 1, opacity: 0 },
        }}
        transition={{ duration: 0.5 }}
        className="absolute inset-0 bg-violet-500/50 rounded-full blur-sm"
      />

      {/* Particles */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          variants={{
            hover: {
              x: [0, (Math.random() - 0.5) * 60],
              y: [0, (Math.random() - 0.5) * 60],
              opacity: [1, 0],
              scale: [0.5, 0],
              transition: {
                duration: 0.8,
                repeat: Infinity,
                delay: i * 0.1,
                ease: 'easeOut',
              },
            },
            initial: { opacity: 0, x: 0, y: 0 },
          }}
          className="absolute h-1 w-1 rounded-full bg-cyan-400 top-2.5 left-2.5"
        />
      ))}
    </motion.div>
  );
};

export default function ProfileHomePage() {
  const { userId } = useParams();
  const { walletAddress, isAuthenticated } = useAuthStore();
  const location = useLocation();

  // Use userId from route params, fallback to walletAddress
  const profileUserId = userId || walletAddress;

  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
  } = useGetUserById(profileUserId);

  const [activeTab, setActiveTab] = useState('discussions');
  const [highlightedDiscussion, setHighlightedDiscussion] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showMissionModal, setShowMissionModal] = useState(false);
  const [missionStatement, setMissionStatement] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showBlockchainInfo, setShowBlockchainInfo] = useState(false);
  const [showFullWallet, setShowFullWallet] = useState(false);
  const [activityFilter, setActivityFilter] = useState('all');
  const [badgeGroupFilter, setBadgeGroupFilter] = useState('all');

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const { data: articlesData, isLoading: isLoadingArticles } =
    useArticlesByCreator(profileUserId);
  const { data: userComments, isLoading: isLoadingUserComments } =
    useGetCommentsByUser(profileUserId);

  // Get interactions GIVEN by the user (actions they performed)
  const { data: interactionsGiven, isLoading: isLoadingInteractionsGiven } =
    useGetInteractionsGivenByUser(user?.oid);

  // View stats (unique, power, standard views given and received)
  const { data: viewStats, isLoading: isLoadingViewStats } =
    useGetViewStats(profileUserId);

  // Check if viewing own profile (for badge sync)
  // Use user?.creator for comparison to ensure we're comparing the same field
  const isOwnProfile = walletAddress && user?.creator &&
    walletAddress.toLowerCase() === user.creator.toLowerCase();

  // Badges - get all badges and user's earned badges
  // Use user?.creator (the wallet address from the fetched user) for consistency
  // Enable sync when viewing own profile to catch any retroactive badges
  const {
    badges: allBadges,
    groupedBadges,
    isLoading: isLoadingAllBadges,
  } = useBadges();
  const {
    badges: userBadges,
    featuredBadges,
    titleBadge,
    toggleFeatured,
    setTitle,
    removeTitle,
    syncBadges,
    isSyncing,
    syncInfo,
    isLoading: isLoadingUserBadges,
  } = useUserBadges(user?.creator, { syncOnMount: isOwnProfile });

  // Create a set of earned badge slugs for quick lookup
  const earnedBadgeSlugs = new Set(
    userBadges.map((ub) => ub.badgeSlug || ub.badge?.slug)
  );

  // Fetch discussions by creator (server-side filtering for security)
  // Use user?.creator (wallet address from fetched user) for consistency with backend
  const { useGetAllDiscussions, deleteDiscussion } = useForum();
  const { data: userDiscussions, isLoading: isLoadingDiscussions } =
    useGetAllDiscussions({ creator: user?.creator });
  const discussions = userDiscussions || [];
  const deleteDiscussionMutation = deleteDiscussion ? deleteDiscussion() : null;

  // Check if viewing own profile
  const isCurrentUser = user && walletAddress === user.creator;

  // Activity type options for filter
  const activityTypeOptions = {
    all: '',
    discussions:
      'view_discussion,create_discussion,edit_discussion,delete_discussion,restore_discussion',
    comments: 'comment,edit_comment,delete_comment',
    votes: 'upvote,downvote,unupvote,undownvote,like,dislike',
    social: 'follow,unfollow',
    admin: 'pin_discussion,unpin_discussion',
  };

  // Fetch recent activity logs - only show for current user viewing their own profile
  const { data: recentActivity, isLoading: isLoadingActivity } = useQuery({
    queryKey: ['userActivity', user?.creator, activityFilter],
    queryFn: async () => {
      if (!user?.creator) return [];
      const params = { limit: 20 };
      // Only add activityTypes param if not 'all'
      if (activityFilter !== 'all' && activityTypeOptions[activityFilter]) {
        params.activityTypes = activityTypeOptions[activityFilter];
      }
      const response = await axios.get(
        API_ENDPOINTS.FORUM.ACTIVITY.GET_USER_ACTIVITY(user.creator),
        { params }
      );
      return response.data.activities || [];
    },
    enabled: !!user?.creator && isCurrentUser,
  });

  const articles = articlesData || [];

  // Get user stats - use interactions GIVEN for upvotes/downvotes/comments
  // Use user object for discussions created
  const userStats = {
    discussions: user?.discussions ?? 0,
    discussionComments: interactionsGiven?.comments ?? 0,
    upvotes: interactionsGiven?.upvotesGiven ?? 0,
    downvotes: interactionsGiven?.downvotesGiven ?? 0,
    likes: interactionsGiven?.likesGiven ?? 0,
    dislikes: interactionsGiven?.dislikesGiven ?? 0,
    reputation:
      (interactionsGiven?.upvotesGiven ?? 0) -
      (interactionsGiven?.downvotesGiven ?? 0),
  };

  // Pagination Logic
  const totalPages = Math.ceil(discussions.length / itemsPerPage);
  const paginatedDiscussions = discussions.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (page) => {
    setCurrentPage(page);
    const element = document.getElementById('discussions-list');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    if (user?.missionStatement) {
      setMissionStatement(user.missionStatement);
    }
  }, [user]);

  // Handle returning from a discussion with highlight
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const returnFrom = params.get('from');
    const returnPage = params.get('page');

    if (returnFrom && discussions.length > 0) {
      setActiveTab('discussions');

      // Restore the page number if provided
      if (returnPage) {
        const pageNum = parseInt(returnPage, 10);
        if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
          setCurrentPage(pageNum);
        }
      }

      setHighlightedDiscussion(returnFrom);

      setTimeout(() => {
        const element = document.getElementById(`discussion-${returnFrom}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);

      setTimeout(() => {
        setHighlightedDiscussion(null);
      }, 3000);
    }
  }, [location.search, discussions.length, totalPages]);

  const updateProfileMutation = useUpdateUserProfile(profileUserId);

  const handleSaveProfile = async (updatedProfile) => {
    try {
      await updateProfileMutation.mutateAsync(updatedProfile);
    } catch (error) {
      console.error('Failed to update profile:', error);
      throw error;
    }
  };

  const handleSaveMissionStatement = (updatedStatement) => {
    setMissionStatement(updatedStatement);
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (isLoadingUser) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <Navbar />
        <div className="flex-1 flex justify-center items-center">
          <div className="text-center">
            <Loader2 className="animate-spin h-12 w-12 text-violet-500 mx-auto mb-4" />
            <p className="text-zinc-400 font-medium">Loading profile...</p>
          </div>
        </div>
      </div>
    );
  }

  if (userError || !user) {
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <Navbar />
        <div className="flex-1 flex justify-center items-center">
          <div className="bg-zinc-900/50 rounded-xl p-8 shadow-sm border border-white/10 backdrop-blur-sm text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mb-4 mx-auto" />
            <h2 className="text-xl font-semibold text-white mb-2">
              Profile Not Found
            </h2>
            <p className="text-zinc-400">
              We couldn't load this user profile. Please try again later.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (
    (user && user.isVerified === false) ||
    (user && (!user.bio || user.bio === '')) ||
    (user && (!user.username || user.username === ''))
  ) {
    return (
      <div className="flex min-h-screen flex-col bg-black">
        <Navbar />
        <main className="flex-1 flex items-center justify-center px-4 py-20 relative z-10">
          <div className="fixed inset-0 z-0 pointer-events-none">
            <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
            <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
          </div>
          <div className="max-w-md mx-auto text-center relative z-10">
            <div className="bg-zinc-900/80 backdrop-blur-xl rounded-3xl p-8 shadow-2xl border border-white/10">
              <div className="h-20 w-20 rounded-full bg-gradient-to-r from-violet-500 to-cyan-600 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-violet-500/20">
                <User className="h-10 w-10 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-white mb-4">
                Complete Your Profile
              </h1>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                To access all features and start creating content, please
                complete your registration by adding your username and bio.
              </p>
              <div className="space-y-3">
                <Button
                  asChild
                  className="w-full bg-white text-black hover:bg-zinc-200 h-12 rounded-xl font-bold transition-all hover:scale-105"
                >
                  <Link to="/complete-registration">Complete Registration</Link>
                </Button>
                <Button
                  variant="outline"
                  asChild
                  className="w-full h-12 rounded-xl border-white/10 text-white hover:bg-white/5"
                >
                  <Link to="/">Back to Home</Link>
                </Button>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white selection:bg-violet-500/30 font-sans flex flex-col relative">
      {/* Background Image Fade Effect */}
      {user.backgroundImage &&
        !user.moderation?.profileRestrictions?.backgroundRestricted && (
          <div className="absolute top-0 left-0 right-0 h-[85vh] z-0 pointer-events-none overflow-hidden">
            <img
              src={user.backgroundImage}
              alt="Background"
              className="w-full h-full object-cover opacity-60"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/40 to-black" />
          </div>
        )}

      {/* Background Blobs (if no image or underneath) */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/10 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/10 blur-[120px]" />
      </div>

      <Navbar />

      <main className="flex-1 relative z-10 container mx-auto px-4 py-8 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left Sidebar - Profile & Nav */}
          <div className="lg:col-span-4 xl:col-span-3 space-y-6">
            {/* Profile Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-zinc-900/50 border border-white/10 rounded-3xl overflow-hidden backdrop-blur-sm sticky top-24"
            >
              <div className="p-6">
                {/* Avatar - Full Width */}
                <div
                  className="relative w-full aspect-square rounded-2xl overflow-hidden border-4 border-zinc-800 shadow-2xl group cursor-pointer"
                  onClick={() => setShowBlockchainInfo(true)}
                >
                  {user.moderation?.profileRestrictions?.avatarRestricted ? (
                    <div className="h-full w-full flex items-center justify-center bg-zinc-800">
                      <ShieldAlert className="h-16 w-16 text-red-500" />
                    </div>
                  ) : (
                    <>
                      <motion.img
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.4 }}
                        src={getUserAvatar(user)}
                        alt={user.username}
                        className="h-full w-full object-cover"
                        onError={(e) => {
                          // Try local fallback if CDN fails
                          if (e.target.src !== MEDIA_DEFAULTS.LOCAL_PROFILE_AVATAR) {
                            e.target.src = MEDIA_DEFAULTS.LOCAL_PROFILE_AVATAR;
                          }
                        }}
                      />
                      {/* Hover Overlay Effect */}
                      <div className="absolute inset-0 bg-violet-500/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center backdrop-blur-[2px]">
                        <div className="bg-black/60 p-3 rounded-full border border-white/20">
                          <Box className="h-6 w-6 text-white" />
                        </div>
                      </div>
                    </>
                  )}

                  {/* Verified Badge Overlay */}
                  {user.isVerified && (
                    <div className="absolute bottom-4 right-4 bg-cyan-500 text-white p-1.5 rounded-full shadow-lg border-2 border-zinc-900">
                      <CheckCircle2 className="h-5 w-5" />
                    </div>
                  )}
                </div>

                {/* User Info */}
                <div className="mt-6 space-y-2 text-center">
                  <h1 className="text-2xl font-bold text-white">
                    {user.name || user.username}
                  </h1>
                  <p className="text-sm font-medium text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">
                    @{user.username}
                  </p>
                  {user.moderation?.profileRestrictions?.bioRestricted ? (
                    <p className="text-sm text-red-400 italic pt-2">
                      Bio restricted by administrator
                    </p>
                  ) : (
                    <p className="text-sm text-zinc-400 leading-relaxed pt-2">
                      {user.bio || 'No bio yet.'}
                    </p>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-6 flex flex-col gap-3">
                  {isCurrentUser ? (
                    <>
                      <Button
                        onClick={() => setShowEditModal(true)}
                        className="w-full bg-white text-black hover:bg-zinc-200 font-semibold rounded-xl transition-all hover:scale-105"
                      >
                        Edit Profile
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full rounded-xl border-white/10 hover:bg-white/5 text-white gap-2"
                        onClick={() => {
                          const profileUrl = `${window.location.origin}/profile/${profileUserId}`;
                          const text = `Check out ${
                            user?.username || 'this profile'
                          } on Talktrail!`;
                          const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                            text
                          )}&url=${encodeURIComponent(profileUrl)}`;
                          window.open(
                            twitterUrl,
                            '_blank',
                            'noopener,noreferrer'
                          );
                        }}
                      >
                        <Share2 className="h-4 w-4" />
                        Share Profile
                      </Button>
                    </>
                  ) : (
                    <div className="flex gap-2">
                      <Button className="flex-1 bg-violet-600 hover:bg-violet-700 text-white rounded-xl font-semibold transition-all hover:scale-105">
                        <Users className="h-4 w-4 mr-2" />
                        Follow
                      </Button>
                      <Button
                        variant="outline"
                        className="rounded-xl border-white/10 hover:bg-white/5 text-white px-3"
                      >
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                      {user?.role !== 'admin' && user?.role !== 'moderator' && (
                        <ReportDialog
                          entityType="user"
                          entityId={user?._id}
                          entityOID={user?.oid}
                          contentPreview={`Profile: ${
                            user?.username || 'Unknown User'
                          }`}
                          trigger={
                            <Button
                              variant="outline"
                              className="rounded-xl border-white/10 text-zinc-400 hover:text-red-400 hover:bg-white/5 px-3"
                            >
                              <Flag className="h-4 w-4" />
                            </Button>
                          }
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Divider */}
                <div className="my-6 h-px bg-white/5" />

                {/* Dashboard Nav */}
                <nav className="space-y-1">
                  <button
                    onClick={() => setActiveTab('discussions')}
                    className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                      activeTab === 'discussions'
                        ? 'bg-white/10 text-white font-medium shadow-inner'
                        : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Layout className="h-4 w-4" />
                      <span>Discussions</span>
                    </div>
                    <span className="text-xs bg-zinc-800 px-2 py-0.5 rounded-full text-zinc-400 border border-white/5">
                      {discussions.length}
                    </span>
                  </button>
                  <button
                    onClick={() => setActiveTab('activity')}
                    className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                      activeTab === 'activity'
                        ? 'bg-white/10 text-white font-medium shadow-inner'
                        : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Activity className="h-4 w-4" />
                      <span>Activity</span>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab('badges')}
                    className={`w-full flex items-center justify-between p-3 rounded-xl transition-all ${
                      activeTab === 'badges'
                        ? 'bg-white/10 text-white font-medium shadow-inner'
                        : 'text-zinc-400 hover:bg-white/5 hover:text-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Award className="h-4 w-4" />
                      <span>Badges</span>
                    </div>
                    <span className="text-xs bg-zinc-800 px-2 py-0.5 rounded-full text-zinc-400 border border-white/5">
                      {userBadges.length}/{allBadges.length}
                    </span>
                  </button>
                </nav>

                {/* User Details */}
                <div className="mt-6 space-y-3 pt-6 border-t border-white/5">
                  <div className="flex items-center gap-3 text-xs text-zinc-500">
                    <Globe className="h-3 w-3" />
                    <span className="capitalize">
                      <span className="inline-flex items-center gap-1.5">
                        <span
                          className={`h-2 w-2 rounded-full ${
                            user.status === 'suspended'
                              ? 'bg-red-500'
                              : user.status === 'banned'
                              ? 'bg-red-700'
                              : 'bg-emerald-500'
                          }`}
                        />
                        {user.status || 'active'}
                      </span>{' '}
                      Member
                    </span>
                  </div>
                  {user.creator && (
                    <div
                      className="flex items-center gap-3 text-xs text-zinc-500 cursor-pointer hover:text-zinc-300 transition-colors group"
                      onClick={() => {
                        if (showFullWallet) {
                          setShowFullWallet(false);
                        } else {
                          setShowFullWallet(true);
                          navigator.clipboard.writeText(user.creator);
                          toast.success('Wallet address copied!');
                        }
                      }}
                      title={
                        showFullWallet
                          ? 'Click to collapse'
                          : 'Click to copy full address'
                      }
                    >
                      <Settings className="h-3 w-3" />
                      <span className="font-mono break-all">
                        {showFullWallet
                          ? user.creator
                          : `${user.creator.slice(0, 6)}...${user.creator.slice(
                              -4
                            )}`}
                      </span>
                      <Copy className="h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-8 xl:col-span-9 space-y-6">
            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                {
                  label: 'Discussions',
                  value: userStats.discussions,
                  icon: MessageSquare,
                  color: 'text-violet-400',
                  bg: 'bg-violet-500/10',
                  tooltip: 'Total discussions you have created',
                },
                {
                  label: 'Comments',
                  value: userStats.discussionComments,
                  icon: MessageSquare,
                  color: 'text-cyan-400',
                  bg: 'bg-cyan-500/10',
                  tooltip: 'Total comments you have made',
                },
                {
                  label: 'Upvotes',
                  value: userStats.upvotes,
                  icon: ThumbsUp,
                  color: 'text-emerald-400',
                  bg: 'bg-emerald-500/10',
                  tooltip: 'Upvotes you have given to others',
                },
                {
                  label: 'Reputation',
                  value: userStats.reputation,
                  icon: Zap,
                  color: 'text-yellow-400',
                  bg: 'bg-yellow-500/10',
                  tooltip: 'Your activity score (upvotes - downvotes given)',
                },
                {
                  label: 'Unique Views',
                  value: viewStats?.given?.unique ?? 0,
                  icon: Eye,
                  color: 'text-orange-400',
                  bg: 'bg-orange-500/10',
                  tooltip: 'Content you viewed for the first time',
                },
                {
                  label: 'Power Views',
                  value: viewStats?.given?.power ?? 0,
                  icon: Eye,
                  color: 'text-pink-400',
                  bg: 'bg-pink-500/10',
                  tooltip: 'Repeat views on content (after 30+ minutes)',
                },
                {
                  label: 'View Power',
                  value: viewStats?.given?.viewPower ?? 0,
                  icon: Zap,
                  color: 'text-purple-400',
                  bg: 'bg-purple-500/10',
                  tooltip: 'Total View Power (Unique×10 + Power×2)',
                },
              ].map((stat, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                  className="bg-zinc-900/50 border border-white/10 rounded-2xl p-3 sm:p-4 backdrop-blur-sm hover:bg-zinc-900/80 transition-all group relative"
                  title={stat.tooltip}
                >
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    <div
                      className={`p-1.5 sm:p-2 rounded-lg ${stat.bg} ${stat.color} group-hover:scale-110 transition-transform flex-shrink-0`}
                    >
                      <stat.icon className="h-3 w-3 sm:h-4 sm:w-4" />
                    </div>
                    <span className="text-zinc-400 text-[10px] sm:text-xs font-medium uppercase tracking-wider truncate">
                      {stat.label}
                    </span>
                  </div>
                  <div className="text-xl sm:text-2xl font-bold text-white pl-1">
                    {stat.value}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Content Tabs */}
            <div className="bg-zinc-900/50 border border-white/10 rounded-3xl p-6 backdrop-blur-sm min-h-[500px]">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  {activeTab === 'discussions' ? (
                    <>
                      <LayoutGrid className="h-5 w-5 text-violet-400" />
                      Recent Discussions
                    </>
                  ) : activeTab === 'badges' ? (
                    <>
                      <Award className="h-5 w-5 text-amber-400" />
                      Badge Progress
                    </>
                  ) : (
                    <>
                      <Activity className="h-5 w-5 text-cyan-400" />
                      Recent Activity
                    </>
                  )}
                </h2>
                {activeTab === 'badges' && (
                  <Select
                    value={badgeGroupFilter}
                    onValueChange={setBadgeGroupFilter}
                  >
                    <SelectTrigger className="w-[160px] bg-zinc-900 border-white/10 text-white">
                      <SelectValue placeholder="Filter by group" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-white/10">
                      <SelectItem value="all">All Groups</SelectItem>
                      {Object.keys(groupedBadges).map((group) => (
                        <SelectItem key={group} value={group}>
                          {getBadgeGroupName(group)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {activeTab === 'discussions' && (
                <div className="space-y-4" id="discussions-list">
                  {paginatedDiscussions.length > 0 ? (
                    <>
                      {paginatedDiscussions.map((discussion) => (
                        <div
                          key={discussion.oid}
                          id={`discussion-${discussion.oid}`}
                          className={`relative group rounded-2xl border p-5 transition-all duration-300 ${
                            highlightedDiscussion === discussion.oid
                              ? 'bg-violet-500/10 border-violet-500/50 shadow-[0_0_30px_rgba(139,92,246,0.1)]'
                              : 'bg-black/20 border-white/5 hover:border-white/10 hover:bg-black/40'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge
                                  variant="outline"
                                  className="bg-white/5 border-white/10 text-zinc-400 hover:bg-white/10"
                                >
                                  {discussion.category}
                                </Badge>
                                <span className="text-xs text-zinc-500 flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {new Date(
                                    discussion.createdAt
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                              <Link
                                to={`${getDiscussionPath(
                                  discussion
                                )}?returnTo=/profile/${profileUserId}&page=${currentPage}`}
                                className="block group-hover:translate-x-1 transition-transform duration-200"
                              >
                                <h3 className="text-lg font-semibold text-white mb-2 group-hover:text-violet-400 transition-colors line-clamp-1">
                                  {discussion.title}
                                </h3>
                                <p className="text-zinc-400 text-sm line-clamp-2 mb-4">
                                  {discussion.description}
                                </p>
                              </Link>
                              <div className="flex items-center gap-4 text-xs text-zinc-500">
                                <span className="flex items-center gap-1.5">
                                  <Eye className="h-3.5 w-3.5" />
                                  {discussion.views || 0}
                                </span>
                                <span className="flex items-center gap-1.5">
                                  <MessageSquare className="h-3.5 w-3.5" />
                                  {discussion.replies || 0}
                                </span>
                                <span className="flex items-center gap-1.5">
                                  <ThumbsUp className="h-3.5 w-3.5" />
                                  {discussion.upvotes || 0}
                                </span>
                              </div>
                            </div>

                            {isCurrentUser && (
                              <div className="flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  asChild
                                  className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-white/10"
                                >
                                  <Link
                                    to={`${getDiscussionPath(
                                      discussion
                                    )}?returnTo=/profile/${profileUserId}&page=${currentPage}`}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Link>
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8 text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                                  onClick={() => {
                                    setDeleteTarget(discussion);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}

                      {totalPages > 1 && (
                        <div className="pt-4 flex justify-center border-t border-white/5 mt-6">
                          <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={handlePageChange}
                            showPageCount={true}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-center py-20 bg-black/20 rounded-2xl border border-dashed border-white/10">
                      <MessageSquare className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">
                        No discussions yet
                      </h3>
                      <p className="text-zinc-500 max-w-sm mx-auto mb-6">
                        Start a conversation with the community by creating your
                        first discussion.
                      </p>
                      {isCurrentUser && (
                        <Button
                          asChild
                          className="bg-violet-600 hover:bg-violet-700 text-white"
                        >
                          <Link to="/forum/discussion/create">
                            Create Discussion
                          </Link>
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'activity' && (
                <div className="space-y-4">
                  {/* Activity Filter */}
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-zinc-500">
                      Filter by activity type
                    </p>
                    <Select
                      value={activityFilter}
                      onValueChange={setActivityFilter}
                    >
                      <SelectTrigger className="w-40 bg-black/20 border-white/10 text-white">
                        <SelectValue placeholder="All Activity" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-white/10">
                        <SelectItem value="all">All Activity</SelectItem>
                        <SelectItem value="discussions">Discussions</SelectItem>
                        <SelectItem value="comments">Comments</SelectItem>
                        <SelectItem value="votes">Votes</SelectItem>
                        <SelectItem value="social">Social</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {isLoadingActivity ? (
                    <div className="text-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-violet-500 mx-auto" />
                    </div>
                  ) : recentActivity && recentActivity.length > 0 ? (
                    <div className="relative min-h-[200px]">
                      {/* Timeline Line */}
                      <div className="absolute left-0 right-0 top-8 h-px bg-white/10" />

                      <div className="flex gap-8 overflow-x-auto pb-8 pt-2 px-4 custom-scrollbar">
                        {recentActivity.map((activity, index) => (
                          <div
                            key={index}
                            className="relative flex-shrink-0 w-64 pt-6 group"
                          >
                            <div className="absolute left-1/2 -top-3 -translate-x-1/2 z-10">
                              <ParticleDot />
                            </div>
                            <div className="bg-black/20 border border-white/5 rounded-xl p-4 hover:bg-black/40 transition-colors mt-4 text-center group-hover:border-violet-500/30">
                              <div className="flex flex-col items-center gap-1 mb-2">
                                <span className="text-sm font-medium text-white capitalize bg-white/5 px-2 py-0.5 rounded-full">
                                  {activity.activityType.replace(/_/g, ' ')}
                                </span>
                                <span className="text-xs text-zinc-500">
                                  {new Date(
                                    activity.createdAt || activity.timestamp
                                  ).toLocaleDateString()}
                                </span>
                              </div>
                              <p className="text-sm text-zinc-400 line-clamp-2">
                                {activity.metadata?.title ||
                                  activity.metadata?.contentPreview ||
                                  activity.metadata?.commentOid ||
                                  'Activity recorded'}
                              </p>
                              {activity.metadata?.category && (
                                <span className="text-xs text-violet-400 mt-1 block">
                                  {activity.metadata.category}
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-20 bg-black/20 rounded-2xl border border-dashed border-white/10">
                      <Activity className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">
                        No recent activity
                      </h3>
                      <p className="text-zinc-500">
                        Your recent actions will appear here.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Badges Tab */}
              {activeTab === 'badges' && (
                <div className="space-y-6">
                  {/* Overall Progress */}
                  <div className="bg-black/20 rounded-2xl border border-white/5 p-5">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm text-zinc-400">
                        Overall Progress
                      </span>
                      <span className="text-sm font-medium text-white">
                        {userBadges.length} / {allBadges.length} badges earned
                      </span>
                    </div>
                    <Progress
                      value={
                        allBadges.length > 0
                          ? (userBadges.length / allBadges.length) * 100
                          : 0
                      }
                      className="h-2 bg-zinc-800"
                    />
                    <div className="flex items-center justify-between mt-3 text-xs text-zinc-500">
                      <span>
                        {Math.round(
                          (userBadges.length / Math.max(allBadges.length, 1)) *
                            100
                        )}
                        % complete
                      </span>
                      <Link
                        to="/forum/badges"
                        className="text-violet-400 hover:text-violet-300 flex items-center gap-1"
                      >
                        View all badges <ChevronRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>

                  {/* Featured Badges */}
                  {featuredBadges.length > 0 && (
                    <div className="bg-black/20 rounded-2xl border border-white/5 p-5">
                      <h3 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                        <Star className="h-4 w-4 text-yellow-500" />
                        Featured Badges
                      </h3>
                      <div className="flex flex-wrap gap-3">
                        {featuredBadges.map((userBadge) => {
                          const badgeData = userBadge.badge || userBadge;
                          const tierStyles = getBadgeTierStyles(badgeData.tier);
                          return (
                            <div
                              key={userBadge._id || userBadge.badgeSlug}
                              className={cn(
                                'flex items-center gap-2 px-3 py-2 rounded-xl border',
                                tierStyles.border,
                                'bg-black/30'
                              )}
                            >
                              <div
                                className={cn(
                                  'w-8 h-8 rounded-lg flex items-center justify-center text-lg',
                                  tierStyles.bg
                                )}
                              >
                                {badgeData.icon}
                              </div>
                              <span className="text-sm text-white font-medium">
                                {badgeData.name}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Badge Groups */}
                  {isLoadingAllBadges || isLoadingUserBadges ? (
                    <div className="text-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-violet-500 mx-auto" />
                    </div>
                  ) : (
                    Object.entries(groupedBadges)
                      .filter(
                        ([group]) =>
                          badgeGroupFilter === 'all' ||
                          group === badgeGroupFilter
                      )
                      .map(([group, badges]) => {
                        const earnedInGroup = badges.filter((b) =>
                          earnedBadgeSlugs.has(b.slug)
                        ).length;
                        const groupProgress =
                          (earnedInGroup / badges.length) * 100;

                        return (
                          <div
                            key={group}
                            className="bg-black/20 rounded-2xl border border-white/5 p-5"
                          >
                            {/* Group Header */}
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="text-sm font-medium text-white flex items-center gap-2">
                                <Award className="h-4 w-4 text-violet-400" />
                                {getBadgeGroupName(group)}
                              </h3>
                              <span className="text-xs text-zinc-500">
                                {earnedInGroup}/{badges.length}
                              </span>
                            </div>

                            {/* Group Progress */}
                            <Progress
                              value={groupProgress}
                              className="h-1.5 bg-zinc-800 mb-4"
                            />

                            {/* Badges Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {badges.map((badge) => {
                                const isEarned = earnedBadgeSlugs.has(
                                  badge.slug
                                );
                                const tierStyles = getBadgeTierStyles(
                                  badge.tier
                                );
                                const userBadge = userBadges.find(
                                  (ub) =>
                                    (ub.badgeSlug || ub.badge?.slug) ===
                                    badge.slug
                                );

                                return (
                                  <div
                                    key={badge.slug}
                                    className={cn(
                                      'relative flex items-start gap-3 p-3 rounded-xl border transition-all',
                                      isEarned
                                        ? 'bg-black/40 border-white/10 hover:border-white/20'
                                        : 'bg-black/20 border-white/5 opacity-60'
                                    )}
                                  >
                                    {/* Badge Icon */}
                                    <div
                                      className={cn(
                                        'w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0',
                                        isEarned ? tierStyles.bg : 'bg-zinc-800'
                                      )}
                                    >
                                      {isEarned ? (
                                        badge.icon
                                      ) : (
                                        <Lock className="h-4 w-4 text-zinc-600" />
                                      )}
                                    </div>

                                    {/* Badge Info */}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <span
                                          className={cn(
                                            'text-sm font-medium truncate',
                                            isEarned
                                              ? 'text-white'
                                              : 'text-zinc-500'
                                          )}
                                        >
                                          {badge.name}
                                        </span>
                                        {isEarned && (
                                          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                                        )}
                                      </div>
                                      <p className="text-xs text-zinc-500 line-clamp-1 mt-0.5">
                                        {badge.description}
                                      </p>
                                      {isEarned && userBadge?.grantedAt && (
                                        <p className="text-[10px] text-zinc-600 mt-1">
                                          Earned{' '}
                                          {new Date(
                                            userBadge.grantedAt
                                          ).toLocaleDateString()}
                                        </p>
                                      )}
                                    </div>

                                    {/* Tier Badge */}
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        'text-[9px] capitalize flex-shrink-0',
                                        isEarned
                                          ? badge.tier === 'gold'
                                            ? 'border-yellow-500/50 text-yellow-400'
                                            : badge.tier === 'silver'
                                            ? 'border-slate-400/50 text-slate-300'
                                            : badge.tier === 'bronze'
                                            ? 'border-orange-500/50 text-orange-400'
                                            : 'border-violet-500/50 text-violet-400'
                                          : 'border-zinc-700 text-zinc-600'
                                      )}
                                    >
                                      {badge.tier}
                                    </Badge>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })
                  )}

                  {/* Empty State */}
                  {!isLoadingAllBadges && allBadges.length === 0 && (
                    <div className="text-center py-20 bg-black/20 rounded-2xl border border-dashed border-white/10">
                      <Award className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">
                        No badges available
                      </h3>
                      <p className="text-zinc-500">
                        Badges will appear here once they're available.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Modals */}
      <EditProfileModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        user={user}
        onSave={handleSaveProfile}
      />

      <MissionStatementModal
        isOpen={showMissionModal}
        onClose={() => setShowMissionModal(false)}
        currentStatement={missionStatement}
        onSave={handleSaveMissionStatement}
      />

      {/* Blockchain Info Dialog */}
      <Dialog open={showBlockchainInfo} onOpenChange={setShowBlockchainInfo}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Box className="h-5 w-5 text-violet-500" />
              Blockchain Identity
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Verified on-chain asset information for this profile.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div className="p-4 rounded-xl bg-black/40 border border-white/5 space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Hash className="h-3 w-3" /> Asset OID
                </label>
                <div className="flex items-center justify-between bg-zinc-900/50 p-2 rounded-lg border border-white/5 group">
                  <code className="text-sm text-violet-300 font-mono truncate max-w-[280px]">
                    {user.oid || '0x71C...9A23'}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-zinc-500 hover:text-white"
                    onClick={() => handleCopy(user.oid || '0x71C...9A23')}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-zinc-500 uppercase tracking-wider flex items-center gap-1.5">
                  <Fingerprint className="h-3 w-3" /> UAL (Universal Asset
                  Locator)
                </label>
                <div className="flex items-center justify-between bg-zinc-900/50 p-2 rounded-lg border border-white/5 group">
                  <code className="text-sm text-cyan-300 font-mono truncate max-w-[280px]">
                    {user.ual || 'did:dkg:otp:2043/0x5f...2a'}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6 text-zinc-500 hover:text-white"
                    onClick={() =>
                      handleCopy(user.ual || 'did:dkg:otp:2043/0x5f...2a')
                    }
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              <div className="pt-2 flex items-center gap-2 text-xs text-zinc-500">
                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                <span>Verified on OriginTrail DKG</span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This action cannot be undone. This will permanently delete the
              discussion "{deleteTarget?.title}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 hover:bg-zinc-800 text-white">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white border-0"
              onClick={() => {
                if (deleteTarget && deleteDiscussionMutation) {
                  deleteDiscussionMutation.mutate(
                    { discussionOid: deleteTarget.oid },
                    {
                      onSuccess: () => {
                        setDeleteDialogOpen(false);
                        setDeleteTarget(null);
                      },
                    }
                  );
                }
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
